/**
 * Pricing model simulation engine.
 *
 * Pure, side-effect-free function that projects a room's price over a
 * configurable horizon (default 10 days) based on the current form values.
 * No DB access — runs entirely on the client so the chart updates live as
 * the host edits any field.
 */

export interface SimDayPoint {
  day: number;
  date: string;
  price: number;
  explanation: string;
  appliedRules: string[];
  /** Effective minimum price cap for this day (null when no min cap is set). */
  effectiveMinPrice: number | null;
  /** Effective maximum price cap for this day (null when no max cap is set). */
  effectiveMaxPrice: number | null;
}

export interface SimulationParams {
  basePrice: number;

  preChangeBoostPercent: number;
  minPercent: number;
  maxPercent: number;
  changeRatePercent: number;
  changeRateHours: number;

  promoPercent: number;
  promoWeeks: string;

  eomPercent: number;
  eomDays: number;

  vacancyPercent: number;
  vacancyDays: number;

  occupancyPercent: number;
  occupancyPct: number;

  enableEom: boolean;
  enableVacancy: boolean;
  enableOccupancy: boolean;

  startDate?: Date;
  horizon?: number;
}

function promoWeeksToNumber(promoWeeks: string): number {
  if (!promoWeeks || promoWeeks === "No Promotion" || promoWeeks === "No promotion") return 0;
  const m = promoWeeks.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Format a dollar amount for the clamp explanation: whole numbers drop the
 * decimals, otherwise show cents (e.g. $110, $112.50). */
function formatSimMoney(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded)
    ? `$${rounded}`
    : `$${rounded.toFixed(2)}`;
}

/** Format a signed percentage for the clamp explanation (e.g. +10%, -10%). */
function formatSimPercent(percent: number): string {
  const sign = percent >= 0 ? "+" : "";
  return `${sign}${percent}%`;
}

/**
 * Run the price simulation and return one point per day (day 0 = today).
 */
export function simulatePricing(params: SimulationParams): SimDayPoint[] {
  const {
    basePrice,
    preChangeBoostPercent,
    minPercent,
    maxPercent,
    changeRatePercent,
    changeRateHours,
    promoPercent,
    promoWeeks,
    eomPercent,
    eomDays,
    vacancyPercent,
    vacancyDays,
    occupancyPercent,
    occupancyPct,
    enableEom,
    enableVacancy,
    enableOccupancy,
    startDate,
    horizon = 10,
  } = params;

  if (basePrice <= 0) return [];

  const start = startDate ? new Date(startDate) : new Date();
  start.setHours(0, 0, 0, 0);

  const promoWeekCount = promoWeeksToNumber(promoWeeks);
  const promoDays = promoWeekCount * 7;

  const changesPerDay = changeRateHours > 0 ? 24 / changeRateHours : 1;

  const points: SimDayPoint[] = [];

  for (let day = 0; day < horizon; day++) {
    const date = new Date(start);
    date.setDate(start.getDate() + day);

    const dayOfMonth = date.getDate();
    const totalDays = daysInMonth(date.getFullYear(), date.getMonth());
    const daysUntilEom = totalDays - dayOfMonth;

    // Determine whether we are inside the EOM window for this day.
    const isInEomWindow = enableEom && eomDays > 0 && eomPercent !== 0 && daysUntilEom < eomDays;

    // Compute per-day effective caps. During the EOM window each cap shifts
    // up by the EOM percentage (percentage-point addition). Caps are only
    // active when their base percent is non-zero.
    const effectiveMaxPercent = maxPercent !== 0 ? maxPercent + (isInEomWindow ? eomPercent : 0) : null;
    const effectiveMinPercent = minPercent !== 0 ? minPercent + (isInEomWindow ? eomPercent : 0) : null;

    const dayMaxPrice = effectiveMaxPercent !== null ? basePrice * (1 + effectiveMaxPercent / 100) : Infinity;
    const dayMinPrice = effectiveMinPercent !== null ? basePrice * (1 + effectiveMinPercent / 100) : 0;

    const appliedRules: string[] = [];
    const parts: string[] = [];

    let price = basePrice;

    if (preChangeBoostPercent > 0) {
      price *= 1 + preChangeBoostPercent / 100;
      appliedRules.push("pre-change-boost");
    }

    // A positive changeRatePercent is a price REDUCTION each interval
    // (compounds downward from the boosted starting price). A negative value
    // produces an upward movement.
    if (changeRatePercent !== 0 && day > 0) {
      const totalIntervals = day * changesPerDay;
      const factor = Math.pow(1 - changeRatePercent / 100, totalIntervals);
      price *= factor;
      if (changeRatePercent > 0) {
        appliedRules.push("change-rate-down");
        parts.push(`Change rate: −${changeRatePercent}% per ${changeRateHours}h`);
      } else {
        appliedRules.push("change-rate-up");
        parts.push(`Change rate: +${Math.abs(changeRatePercent)}% per ${changeRateHours}h`);
      }
    }

    if (promoDays > 0 && promoPercent > 0 && day < promoDays) {
      price *= 1 - promoPercent / 100;
      appliedRules.push("promo");
      parts.push(`Promotional rate: −${promoPercent}% (${promoWeeks})`);
    }

    // EOM adjustment lifts the price, but the price is then clamped against
    // the already-lifted cap (effectiveMaxPercent) computed above, so the
    // EOM bump is always visible in the chart.
    if (isInEomWindow) {
      price *= 1 + eomPercent / 100;
      appliedRules.push("eom");
      const sign = eomPercent >= 0 ? "+" : "";
      parts.push(`End-of-month adjustment: ${sign}${eomPercent}% (${daysUntilEom}d until EOM)`);
    }

    if (enableVacancy && vacancyDays > 0 && vacancyPercent !== 0 && day >= vacancyDays) {
      price *= 1 + vacancyPercent / 100;
      appliedRules.push("vacancy");
      const sign = vacancyPercent >= 0 ? "+" : "";
      parts.push(`Vacancy adjustment after ${vacancyDays} days: ${sign}${vacancyPercent}%`);
    }

    if (enableOccupancy && occupancyPercent !== 0) {
      price *= 1 + occupancyPercent / 100;
      appliedRules.push("occupancy");
      const sign = occupancyPercent >= 0 ? "+" : "";
      parts.push(`Occupancy adjustment (${occupancyPct}% occupied): ${sign}${occupancyPercent}%`);
    }

    if (dayMinPrice > 0 && price < dayMinPrice) {
      price = dayMinPrice;
      appliedRules.push("min-clamp");
      parts.push(
        `Capped at ${formatSimMoney(dayMinPrice)} — min floor (${formatSimPercent(effectiveMinPercent ?? 0)})`,
      );
    } else if (dayMaxPrice < Infinity && price > dayMaxPrice) {
      price = dayMaxPrice;
      appliedRules.push("max-clamp");
      parts.push(
        `Capped at ${formatSimMoney(dayMaxPrice)} — max ceiling (${formatSimPercent(effectiveMaxPercent ?? 0)})`,
      );
    }

    price = Math.round(price * 100) / 100;

    let explanation: string;
    if (parts.length === 0) {
      explanation = day === 0 && preChangeBoostPercent > 0
        ? `Starting price with +${preChangeBoostPercent}% pre-change boost`
        : day === 0
        ? "Base price — no adjustments on day 0"
        : "Base price — no active adjustments";
    } else {
      explanation = parts.join("; ");
    }

    if (day === 0 && preChangeBoostPercent > 0 && parts.length === 0) {
      explanation = `Starting price with +${preChangeBoostPercent}% pre-change boost`;
    }

    points.push({
      day,
      date: toISODate(date),
      price,
      explanation,
      appliedRules,
      effectiveMinPrice: effectiveMinPercent !== null ? Math.round(dayMinPrice * 100) / 100 : null,
      effectiveMaxPrice: effectiveMaxPercent !== null ? Math.round(dayMaxPrice * 100) / 100 : null,
    });
  }

  return points;
}
